import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Loader2, Plus, ChevronDown, ChevronUp, RefreshCw, AlertTriangle, Camera, Building2, Landmark, Banknote, Clock } from 'lucide-react';
import { assetsLiabilitiesAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { NetAssetsChart } from './dashboard/NetAssetsChart';
import { compositionFieldsFromSeriesPoint } from '../lib/netAssetsChart';
import { monthKeyToLabel } from '../lib/forecastChartUtils';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { useEntityStore } from '../store';
import { AlEntriesTab } from './AlEntriesTab';

// Matches backend UNASSIGNED_ENTITY_SENTINEL (routes/assets_liabilities.py):
// the API requires this exact literal to create an explicit "Unassigned"
// item -- a genuinely omitted entity_id is still rejected server-side.
const UNASSIGNED = 'unassigned';

/**
 * Two fixed-width, right-aligned columns for a native/converted amount pair,
 * used by every balance-sheet row so amounts line up down the page instead of
 * jittering with each row's number length.
 *
 * native == null (a multi-currency parent has no single native figure): the
 * converted total is shown alone in the converted column.
 * nativeCurrency === displayCurrency: converted would equal native, so only
 * the native column is shown (same convention as before this change).
 */
function AmountPair({ native, nativeCurrency, converted, displayCurrency }) {
  const hasNative = native != null && nativeCurrency != null;
  const showConverted = converted != null && (!hasNative || nativeCurrency !== displayCurrency);
  return (
    <div className="flex items-center gap-2 tabular-nums">
      <span className="text-muted-foreground text-right min-w-[88px]">
        {hasNative ? formatCurrency(native, nativeCurrency) : ''}
      </span>
      <span className="font-mono text-right min-w-[88px]">
        {showConverted ? formatCurrency(converted, displayCurrency) : ''}
      </span>
    </div>
  );
}

/**
 * One hierarchy parent (receivable entity / intercompany counterparty) with its
 * currency sublines. A parent with exactly one currency subline collapses to a
 * single row (native muted inline before the converted total, no subline) since
 * the subline would otherwise just repeat the parent. Two or more currencies
 * keep the parent + indented sublines layout.
 */
function HierarchyGroupRow({ label, group, displayCurrency, testId }) {
  const sublines = group.sublines || [];
  if (sublines.length === 1) {
    const s = sublines[0];
    return (
      <div className="flex items-center justify-between text-xs py-0.5" data-testid={testId}>
        <span>{label}</span>
        <AmountPair
          native={s.amount}
          nativeCurrency={s.currency}
          converted={group.converted_total}
          displayCurrency={displayCurrency}
        />
      </div>
    );
  }
  return (
    <div className="py-0.5" data-testid={testId}>
      <div className="flex items-center justify-between text-xs">
        <span>{label}</span>
        <AmountPair native={null} nativeCurrency={null} converted={group.converted_total} displayCurrency={displayCurrency} />
      </div>
      {sublines.map((s, j) => (
        <div key={j} className="flex items-center justify-between text-[11px] pl-3 text-muted-foreground">
          <span>{s.currency}</span>
          <AmountPair native={s.amount} nativeCurrency={s.currency} converted={s.converted_value} displayCurrency={displayCurrency} />
        </div>
      ))}
    </div>
  );
}

/**
 * Aggregate header row for a balance-sheet subsection (Cash, Receivables,
 * Intercompany Receivables/Payables, and per-source/per-counterparty groups).
 * Visually distinct from the detail rows beneath it: heavier weight, a
 * background tint, and a top border, so a reader never confuses an aggregate
 * line with a child line. Optionally collapsible (chevron) for per-company
 * detail groups, default collapsed so aggregates dominate the page.
 */
function AggregateRow({ label, converted, displayCurrency, collapsible, expanded, onToggle, testId }) {
  const Wrapper = collapsible ? 'button' : 'div';
  return (
    <Wrapper
      type={collapsible ? 'button' : undefined}
      onClick={collapsible ? onToggle : undefined}
      className="w-full flex items-center justify-between text-xs font-semibold bg-muted/50 border-t border-border/60 px-2 py-1 rounded-sm mt-1"
      data-testid={testId}
    >
      <span className="flex items-center gap-1">
        {collapsible && (expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
        {label}
      </span>
      <AmountPair native={null} nativeCurrency={null} converted={converted} displayCurrency={displayCurrency} />
    </Wrapper>
  );
}

/**
 * An aggregate header plus its collapsed-by-default children (per-company
 * detail). Used for "Unpaid bookings", "Intercompany Loans", and any other
 * category where multiple company rows would otherwise crowd the page.
 * A group with exactly one child renders that child directly (no redundant
 * one-item wrapper), mirroring the single-currency collapse precedent --
 * unless `alwaysCollapsible` is set, for a source that always has exactly
 * one child (single-entity) but still needs chevron/collapsed-by-default
 * parity with its multi-entity siblings.
 */
function CollapsibleAggregate({ label, converted, displayCurrency, children, testId, alwaysCollapsible }) {
  const [expanded, setExpanded] = useState(false);
  const childArray = React.Children.toArray(children);
  if (childArray.length <= 1 && !alwaysCollapsible) {
    return <div data-testid={testId}>{children}</div>;
  }
  return (
    <div data-testid={testId}>
      <AggregateRow
        label={label}
        converted={converted}
        displayCurrency={displayCurrency}
        collapsible
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        testId={`${testId}-toggle`}
      />
      {expanded && (
        <div className="pl-3 space-y-0.5 mt-0.5" data-testid={`${testId}-children`}>
          {children}
        </div>
      )}
    </div>
  );
}

// Receivable/to-come source -> aggregate label, mirrors backend
// _COLLAPSIBLE_LINE_LABELS (routes/assets_liabilities.py) so the per-company
// wrapper reads the same as the backend's own collapsed-aggregate label.
// bros_invoices reads "Unpaid BR OS invoices" here (matching the Entries
// tab's key/label, AlEntriesTab.jsx) so its collapsible header names the
// category the same way "Unpaid invoices" / "Unpaid bookings" / "Unpaid
// mgmt statements" do; the row description underneath still reads "BR OS
// client invoices, <entity> (<count>)" as it always has.
const SOURCE_LABELS = {
  bros_invoices: 'Unpaid BR OS invoices',
  pennylane_invoices: 'Unpaid invoices',
  bookings: 'Unpaid bookings',
  management_statements: 'Unpaid mgmt statements',
  eliminated_overlap: 'Eliminated overlap',
};

/**
 * Groups entity-parent rows (ReceivableGroup[]) by their `source` field into
 * ordered buckets, each carrying a display total (sum of the already-rounded
 * converted_total figures the API returned -- no re-derivation of the
 * underlying amounts, purely a display-layer regroup). A single-entity view
 * naturally yields one row per source, so this is a no-op there; only the
 * multi-entity aggregate view produces multi-row buckets.
 */
function groupRowsBySource(rows) {
  const order = [];
  const bySource = new Map();
  (rows || []).forEach((r) => {
    // A zero line that only says "all bookings represented by finalized
    // invoices" read as a child of the group above it (Fan, 2026-10-27);
    // the dedup note under the section already says it.
    if (!r.converted_total && r.deduped_count > 0) return;
    const key = r.source;
    if (!bySource.has(key)) {
      const g = { source: key, label: SOURCE_LABELS[key] || r.description || key, rows: [], converted_total: 0 };
      bySource.set(key, g);
      order.push(key);
    }
    const g = bySource.get(key);
    g.rows.push(r);
    g.converted_total += r.converted_total || 0;
  });
  return order.map((k) => bySource.get(k));
}

/**
 * Shared Assets & Liabilities section. Used by both Personal and Entities scopes.
 *
 * Layout: Net Assets headline card, then the Net Assets chart, then two
 * columns (Total Assets box left, Total Liabilities box right), each
 * wrapping its Fixed + Current sub-boxes.
 *
 * Props:
 *   scope: "personal" | "entity" | "group"
 *   entityId: required when scope="entity", null otherwise. "group" is the
 *     read-only business+personal aggregate: manual items are never STORED
 *     under it (they live in one of the two real scopes), so its write
 *     controls -- Add Item and Snapshot Now -- are hidden rather than left
 *     to fail at the API boundary.
 *   displayCurrency: "GBP" | "EUR"
 */
export function AssetsLiabilitiesSection({ scope, entityId, displayCurrency = 'GBP' }) {
  // Group is an aggregate READ scope -- it has no storage of its own.
  const isReadOnlyScope = scope === 'group';
  const entities = useEntityStore((s) => s.entities) ?? [];
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [bsError, setBsError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [manualItems, setManualItems] = useState([]);
  const [monthlySeries, setMonthlySeries] = useState([]);
  const [netAssetsSeries, setNetAssetsSeries] = useState(null);
  const [netAssetsBasis, setNetAssetsBasis] = useState(null);
  const [netAssetsUnclassifiedTypes, setNetAssetsUnclassifiedTypes] = useState([]);
  const [netAssetsUnclassifiedLiabilityTypes, setNetAssetsUnclassifiedLiabilityTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddItem, setShowAddItem] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const [assetsGapExpanded, setAssetsGapExpanded] = useState(false);
  const [liabilitiesGapExpanded, setLiabilitiesGapExpanded] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Every fetch below is independent and settled separately (deferred
      // into a .then() so even a synchronous throw from the API call
      // becomes a rejection here): one of them failing must never mask
      // another's outcome. In particular, balance sheet is required with
      // no fallback -- a failed fetch surfaces an explicit error state
      // instead of silently rendering an undercounted legacy summary that
      // looks like a real balance sheet -- and that must hold even if,
      // say, getMonthlySeries (the legacy chart fallback, unrelated to the
      // balance sheet) also fails in the same load.
      const [catResult, seriesResult, itemsResult, bsResult, netSeriesResult] = await Promise.allSettled([
        Promise.resolve().then(() => assetsLiabilitiesAPI.getCategories()),
        Promise.resolve().then(() => assetsLiabilitiesAPI.getMonthlySeries(scope, entityId, displayCurrency, 12)),
        Promise.resolve().then(() => assetsLiabilitiesAPI.getItems(scope, entityId)),
        Promise.resolve().then(() => assetsLiabilitiesAPI.getBalanceSheet(scope, entityId, displayCurrency)),
        Promise.resolve().then(() => assetsLiabilitiesAPI.getNetAssetsSeries(scope, entityId, displayCurrency, 24, true)),
      ]);

      if (bsResult.status === 'fulfilled') {
        setBalanceSheet(bsResult.value?.data ?? null);
        setBsError(null);
      } else {
        setBalanceSheet(null);
        setBsError(getErrorMessage(bsResult.reason));
      }

      if (catResult.status === 'fulfilled') {
        setCategories(catResult.value?.data ?? []);
      } else {
        toast.error(getErrorMessage(catResult.reason));
      }

      if (itemsResult.status === 'fulfilled') {
        setManualItems(itemsResult.value?.data ?? []);
      } else {
        toast.error(getErrorMessage(itemsResult.reason));
      }

      // Both non-blocking: legacy monthlySeries covers the Net Assets chart
      // if netAssetsSeries fails, same fallback pattern as before.
      setMonthlySeries(seriesResult.status === 'fulfilled' ? (seriesResult.value?.data?.series ?? []) : []);
      const netSeriesData = netSeriesResult.status === 'fulfilled' ? (netSeriesResult.value?.data ?? null) : null;
      setNetAssetsSeries(netSeriesData?.series ?? null);
      setNetAssetsBasis(netSeriesData?.basis ?? null);
      setNetAssetsUnclassifiedTypes(netSeriesData?.unclassified_types ?? []);
      setNetAssetsUnclassifiedLiabilityTypes(netSeriesData?.unclassified_liability_types ?? []);
    } finally {
      setLoading(false);
    }
  }, [scope, entityId, displayCurrency]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSnapshot = async () => {
    setSnapshotting(true);
    try {
      const resp = await assetsLiabilitiesAPI.takeSnapshot(scope, entityId);
      toast.success(`Snapshot ${resp.data?.action ?? 'taken'}`);
      await loadData();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSnapshotting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const bs = balanceSheet;

  return (
    <div className="space-y-6" data-testid="al-section">
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="al-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="entries" data-testid="al-tab-entries">Entries</TabsTrigger>
          <TabsTrigger value="controls" data-testid="al-tab-controls">Controls</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6" data-testid="al-tab-content-overview">
      {bsError ? (
        // Balance sheet is the only source of truth for this section now
        // (the /al/summary fallback undercounted assets, see
        // routes/assets_liabilities.py module docstring). A failed fetch
        // shows an explicit error here, never partial or wrong numbers --
        // but only within this tab: Controls (manual items, categories)
        // is fetched independently and must stay reachable so an operator
        // can fix data that may be the actual cause of the failure.
        <Alert variant="destructive" data-testid="al-balance-sheet-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="space-y-2">
            <p className="font-medium">Balance sheet unavailable</p>
            <p data-testid="al-balance-sheet-error-message">{bsError}</p>
            <Button variant="outline" size="sm" onClick={loadData} data-testid="al-balance-sheet-retry">
              <RefreshCw className="h-4 w-4 mr-1" /> Retry
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <>
      {/* Net Assets headline */}
      <div data-testid="al-headline-cards">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Net Assets</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold" data-testid="bs-net-assets">
              {formatCurrency(bs?.totals?.net_assets ?? 0, displayCurrency)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Unconvertible warning */}
      {bs?.unconvertible?.length > 0 && (
        <div className="rounded-md bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-200" data-testid="al-unconvertible-warning">
          {bs.unconvertible.length} item(s) excluded from totals (unconvertible currency).
          Native values shown inline.
        </div>
      )}

      {/* Net Assets chart: server-computed series only (bs-backed net-assets-series
          when available, legacy monthlySeries as fallback), positioned above the
          Total Assets / Total Liabilities boxes. Shared composition-split chart
          component (Fixed Assets + Current Assets stacked, Liabilities negative,
          Net Assets line) -- same component every dashboard surface renders. */}
      <NetAssetsChart
        chartData={(netAssetsSeries ?? monthlySeries).map((m) => {
          const fields = compositionFieldsFromSeriesPoint(m);
          return {
            month: monthKeyToLabel(m.month),
            _monthKey: m.month,
            fixedNet: fields.fixedNet,
            currentNet: fields.currentNet,
            net: fields.net,
            _isForecast: false,
            _source: m.source,
          };
        })}
        basisCaption={netAssetsSeries ? netAssetsBasis : 'Monthly assets, liabilities, and net assets'}
        isLegacyFallback={!netAssetsSeries}
        displayCurrency={displayCurrency}
      />
      {netAssetsUnclassifiedTypes.length > 0 && (
        <div
          className="flex items-center gap-1.5 rounded-md bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-200"
          data-testid="al-unclassified-asset-types-warning"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {netAssetsUnclassifiedTypes.length} asset type(s) not yet classified as fixed or current for the Net Assets
          chart: {netAssetsUnclassifiedTypes.join(', ')}.
        </div>
      )}
      {netAssetsUnclassifiedLiabilityTypes.length > 0 && (
        <div
          className="flex items-center gap-1.5 rounded-md bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-200"
          data-testid="al-unclassified-liability-types-warning"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {netAssetsUnclassifiedLiabilityTypes.length} liability type(s) not yet classified as fixed or current for
          the Net Assets chart: {netAssetsUnclassifiedLiabilityTypes.join(', ')}.
        </div>
      )}

      {/* Balance Sheet: two-column layout, each column ONE outer Total box
          containing its Fixed + Current sub-boxes (same totals as before;
          this is a pure nesting/visual restructure, no number changes). */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="al-two-columns">
        {/* Assets column */}
        <div className="space-y-4" data-testid="al-assets-column">
          <Card className="border-2 border-green-600/30" data-testid="bs-total-assets-box">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Total Assets</CardTitle>
              <p className="text-2xl font-bold text-green-600" data-testid="al-total-assets">
                {formatCurrency(bs?.totals?.total_assets ?? 0, displayCurrency)}
              </p>
            </CardHeader>
            {bs && (
              <CardContent className="space-y-4">
                {/* Fixed Assets (real estate) */}
                {(() => {
                  const realRows = bs.fixed_assets.rows.filter((r) => r.status !== 'no_valuation');
                  const gapRows = bs.fixed_assets.rows.filter((r) => r.status === 'no_valuation');
                  return (
                    <Card data-testid="bs-fixed-assets">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-1.5">
                          <Building2 className="h-4 w-4 text-green-600" />
                          Fixed Assets
                          <span className="ml-auto font-mono text-sm">
                            {formatCurrency(bs.fixed_assets.subtotal, displayCurrency)}
                          </span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0 space-y-1">
                        {realRows.map((r) => (
                          <div key={r.property_id} className="flex items-center justify-between text-xs py-0.5">
                            <span>{r.property_name || r.property_id}</span>
                            <div className="flex items-center gap-2 text-right">
                              <AmountPair native={r.valuation} nativeCurrency={r.currency} converted={r.converted_value} displayCurrency={displayCurrency} />
                              {r.as_of && (
                                <span className="text-[10px] text-muted-foreground">{r.as_of}</span>
                              )}
                            </div>
                          </div>
                        ))}
                        {gapRows.length > 0 && (
                          <div>
                            <button
                              type="button"
                              className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700"
                              onClick={() => setAssetsGapExpanded((v) => !v)}
                              data-testid="bs-fixed-assets-gap-toggle"
                            >
                              {assetsGapExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                              {gapRows.length} propert{gapRows.length === 1 ? 'y' : 'ies'} without valuation
                            </button>
                            {assetsGapExpanded && (
                              <div className="pl-4 space-y-1 mt-1" data-testid="bs-fixed-assets-gap-rows">
                                {gapRows.map((r) => (
                                  <div key={r.property_id} className="flex items-center justify-between text-xs py-0.5">
                                    <div className="flex items-center gap-1.5">
                                      <span>{r.property_name || r.property_id}</span>
                                      <Badge variant="outline" className="text-[9px] px-1 py-0 text-amber-600 border-amber-300">
                                        No valuation
                                      </Badge>
                                    </div>
                                    <span className="text-muted-foreground italic">--</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {bs.fixed_assets.rows.length === 0 && (
                          <p className="text-xs text-muted-foreground">No owned properties</p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Current Assets (cash + receivables + to-come) */}
                <Card data-testid="bs-current-assets">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-1.5">
                      <Banknote className="h-4 w-4 text-green-600" />
                      Current Assets
                      <span className="ml-auto font-mono text-sm">
                        {formatCurrency(bs.current_assets.subtotal, displayCurrency)}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-1">
                    {/* Cash */}
                    {bs.current_assets.cash.rows.length > 0 && (
                      <div>
                        <AggregateRow
                          label="Cash"
                          converted={bs.current_assets.cash.subtotal}
                          displayCurrency={displayCurrency}
                          testId="bs-aggregate-cash"
                        />
                        <div className="pl-3 mt-0.5">
                          {bs.current_assets.cash.rows.map((r, i) => (
                            <div key={i} className="flex items-center justify-between text-xs py-0.5">
                              <span>Bank balance ({r.currency})</span>
                              <AmountPair native={r.balance} nativeCurrency={r.currency} converted={r.converted_value} displayCurrency={displayCurrency} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Receivables: aggregate header, then one collapsible group per
                        source (Unpaid BR OS invoices, Unpaid invoices, Unpaid
                        bookings, Unpaid mgmt statements), each carrying its own
                        per-company (entity) children behind a chevron -- default
                        collapsed. Single-company sources render directly with no
                        redundant wrapper, EXCEPT bros_invoices: it is only ever
                        populated for one entity (Bonjour Residences) but must still
                        stand as its own collapsible sibling category, chevron and
                        collapsed-by-default parity with the others, not an inline
                        line with no header. */}
                    {bs.current_assets.receivables.rows.length > 0 && (
                      <div>
                        <AggregateRow
                          label="Receivables"
                          converted={bs.current_assets.receivables.subtotal}
                          displayCurrency={displayCurrency}
                          testId="bs-aggregate-receivables"
                        />
                        <div className="pl-3 mt-0.5 space-y-0.5">
                          {groupRowsBySource(bs.current_assets.receivables.rows).map((g) => (
                            // The eliminated-overlap line (FA's own copies of BR OS invoices,
                            // never added to the subtotal) is internal plumbing: hidden here
                            // (Fan, 2026-10-22), still listed on the Entries tab.
                            g.source === 'eliminated_overlap' ? null : (
                              <CollapsibleAggregate
                                key={g.source}
                                label={g.label}
                                converted={g.converted_total}
                                displayCurrency={displayCurrency}
                                testId={`bs-source-group-${g.source}`}
                                alwaysCollapsible
                              >
                                {g.rows.map((r, i) => (
                                  <React.Fragment key={i}>
                                    <HierarchyGroupRow
                                      label={r.description}
                                      group={r}
                                      displayCurrency={displayCurrency}
                                      testId="bs-receivable-group"
                                    />
                                    {r.awaiting_confirmation_count > 0 && (
                                      <p className="text-[10px] text-muted-foreground pl-3 italic" data-testid="bs-receivable-awaiting-confirmation">
                                        of which {formatCurrency(r.awaiting_confirmation_amount, displayCurrency)} ({r.awaiting_confirmation_count}) has suggested matches awaiting confirmation
                                      </p>
                                    )}
                                    {r.unparseable_excluded_count > 0 && (
                                      <p className="text-[10px] text-muted-foreground pl-3 italic" data-testid="bs-receivable-unparseable-excluded">
                                        ({r.unparseable_excluded_count} unparseable excluded)
                                      </p>
                                    )}
                                  </React.Fragment>
                                ))}
                              </CollapsibleAggregate>
                            )
                          ))}
                        </div>
                        {bs.current_assets.receivables.rows.some(r => r.deduped_count > 0) && (
                          <p className="text-[10px] text-muted-foreground mt-1 pl-3" data-testid="bs-dedup-note">
                            {bs.current_assets.receivables.rows.reduce((sum, r) => sum + (r.deduped_count || 0), 0)} bookings represented by finalized invoices
                          </p>
                        )}
                      </div>
                    )}

                    {/* Intercompany receivables: aggregate header, per-counterparty
                        (per-company) children behind a chevron, same collapse
                        pattern as Unpaid bookings above. */}
                    {bs.current_assets.intercompany && (bs.current_assets.intercompany.rows.length > 0 || bs.current_assets.intercompany.eliminated_count > 0) && (
                      <div>
                        <AggregateRow
                          label="Intercompany Receivables"
                          converted={bs.current_assets.intercompany.subtotal}
                          displayCurrency={displayCurrency}
                          testId="bs-aggregate-ic-receivables"
                        />
                        <div className="pl-3 mt-0.5">
                          <CollapsibleAggregate
                            label="Intercompany Loans"
                            converted={bs.current_assets.intercompany.subtotal}
                            displayCurrency={displayCurrency}
                            testId="bs-source-group-intercompany-receivable"
                          >
                            {bs.current_assets.intercompany.rows.map((r, i) => (
                              <HierarchyGroupRow
                                key={i}
                                label={`Intercompany loan, ${r.counterparty_name}`}
                                group={r}
                                displayCurrency={displayCurrency}
                                testId="bs-ic-receivable-group"
                              />
                            ))}
                          </CollapsibleAggregate>
                          {bs.current_assets.intercompany.eliminated_count > 0 && (
                            <div className="flex items-center justify-between text-xs py-0.5 text-muted-foreground italic">
                              <span>Intra-group eliminated ({bs.current_assets.intercompany.eliminated_count})</span>
                              <span className="font-mono">{formatCurrency(bs.current_assets.intercompany.eliminated_total, displayCurrency)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Manual items (Controls tab, al_items): entity assignment shown
                        explicitly per row, including "Unassigned" -- never folded. */}
                    {bs.current_assets.manual && bs.current_assets.manual.rows.length > 0 && (
                      <div>
                        <AggregateRow
                          label="Manual"
                          converted={bs.current_assets.manual.subtotal}
                          displayCurrency={displayCurrency}
                          testId="bs-aggregate-manual-assets"
                        />
                        <div className="pl-3 mt-0.5 space-y-0.5">
                          {bs.current_assets.manual.rows.map((r) => (
                            <div key={r.id} className="flex items-center justify-between text-xs py-0.5" data-testid="bs-manual-asset-row">
                              <span>
                                {r.name}
                                {scope === 'entity' && (
                                  <span className="text-muted-foreground"> ({r.entity_name || 'Unassigned'})</span>
                                )}
                              </span>
                              <AmountPair native={r.native_value} nativeCurrency={r.currency} converted={r.converted_value} displayCurrency={displayCurrency} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* To-come (annotated, visually distinct, NOT in subtotal) */}
                    {bs.current_assets.to_come.rows.length > 0 && (
                      <div className="border-t pt-2 mt-2" data-testid="bs-to-come">
                        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          To Come (not in total)
                        </p>
                        {bs.current_assets.to_come.rows.map((r, i) => (
                          <div key={i} className="flex items-center justify-between text-xs py-0.5 text-muted-foreground">
                            <span>{r.description}</span>
                            <AmountPair native={r.amount} nativeCurrency={r.currency} converted={r.converted_value} displayCurrency={displayCurrency} />
                          </div>
                        ))}
                      </div>
                    )}

                    {bs.current_assets.cash.rows.length === 0 &&
                      bs.current_assets.receivables.rows.length === 0 && !bs.current_assets.receivables.eliminated_count &&
                      (!bs.current_assets.intercompany || (bs.current_assets.intercompany.rows.length === 0 && !bs.current_assets.intercompany.eliminated_count)) &&
                      (!bs.current_assets.manual || bs.current_assets.manual.rows.length === 0) && (
                      <p className="text-xs text-muted-foreground">No current assets</p>
                    )}
                  </CardContent>
                </Card>
              </CardContent>
            )}
          </Card>
        </div>

        {/* Liabilities column */}
        <div className="space-y-4" data-testid="al-liabilities-column">
          <Card className="border-2 border-red-600/30" data-testid="bs-total-liabilities-box">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Total Liabilities</CardTitle>
              <p className="text-2xl font-bold text-red-600" data-testid="al-total-liabilities">
                {formatCurrency(bs?.totals?.total_liabilities ?? 0, displayCurrency)}
              </p>
            </CardHeader>
            {bs && (
              <CardContent className="space-y-4">
                {/* Fixed Liabilities (loans) */}
                {(() => {
                  const realRows = bs.fixed_liabilities.rows.filter((r) => r.status !== 'no_data');
                  const gapRows = bs.fixed_liabilities.rows.filter((r) => r.status === 'no_data');
                  return (
                    <Card data-testid="bs-fixed-liabilities">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-1.5">
                          <Landmark className="h-4 w-4 text-red-600" />
                          Fixed Liabilities
                          <span className="ml-auto font-mono text-sm">
                            {formatCurrency(bs.fixed_liabilities.subtotal, displayCurrency)}
                          </span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0 space-y-1">
                        {realRows.map((r) => (
                          <div key={r.property_id} className="flex items-center justify-between text-xs py-0.5">
                            <div className="flex items-center gap-1.5">
                              <span>{r.property_name || r.property_id}</span>
                              {r.lender && (
                                <span className="text-[10px] text-muted-foreground">({r.lender})</span>
                              )}
                            </div>
                            <AmountPair native={r.outstanding_principal} nativeCurrency={r.currency} converted={r.converted_value} displayCurrency={displayCurrency} />
                          </div>
                        ))}
                        {realRows.some((r) => r.capital_payoff_share) && (
                          <div className="text-[10px] text-muted-foreground pt-1" data-testid="bs-fixed-liabilities-basis">
                            Assumption: {Math.round((realRows.find((r) => r.capital_payoff_share).capital_payoff_share) * 100)}% of every
                            mortgage payment repays capital.
                            {realRows.every((r) => r.bank_table_principal != null && r.currency === realRows[0].currency) && (
                              <> Bank schedules would show{' '}
                                {formatCurrency(
                                  realRows.reduce((a, r) => a + (r.bank_table_principal || 0), 0),
                                  realRows[0].currency,
                                )}{' '}
                                across these loans.
                              </>
                            )}
                          </div>
                        )}
                        {gapRows.length > 0 && (
                          <div>
                            <button
                              type="button"
                              className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700"
                              onClick={() => setLiabilitiesGapExpanded((v) => !v)}
                              data-testid="bs-fixed-liabilities-gap-toggle"
                            >
                              {liabilitiesGapExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                              {gapRows.length} propert{gapRows.length === 1 ? 'y' : 'ies'} without loan data
                            </button>
                            {liabilitiesGapExpanded && (
                              <div className="pl-4 space-y-1 mt-1" data-testid="bs-fixed-liabilities-gap-rows">
                                {gapRows.map((r) => (
                                  <div key={r.property_id} className="flex items-center justify-between text-xs py-0.5">
                                    <div className="flex items-center gap-1.5">
                                      <span>{r.property_name || r.property_id}</span>
                                      {r.lender && (
                                        <span className="text-[10px] text-muted-foreground">({r.lender})</span>
                                      )}
                                      <Badge variant="outline" className="text-[9px] px-1 py-0 text-amber-600 border-amber-300">
                                        No data
                                      </Badge>
                                    </div>
                                    <span className="text-muted-foreground italic">--</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {bs.fixed_liabilities.rows.length === 0 && (
                          <p className="text-xs text-muted-foreground">No owned properties</p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Current Liabilities */}
                <Card data-testid="bs-current-liabilities">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-1.5">
                      Current Liabilities
                      <span className="ml-auto font-mono text-sm">
                        {formatCurrency(bs.current_liabilities.subtotal, displayCurrency)}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {bs.current_liabilities.payables && bs.current_liabilities.payables.rows.length > 0 && (
                      <div>
                        <AggregateRow
                          label="Supplier Invoices"
                          converted={bs.current_liabilities.payables.subtotal}
                          displayCurrency={displayCurrency}
                          testId="bs-aggregate-payables"
                        />
                        <div className="pl-3 mt-0.5">
                          <CollapsibleAggregate
                            label="Unpaid supplier invoices"
                            converted={bs.current_liabilities.payables.subtotal}
                            displayCurrency={displayCurrency}
                            testId="bs-source-group-payables"
                          >
                            {bs.current_liabilities.payables.rows.map((r, i) => (
                              <HierarchyGroupRow
                                key={i}
                                label={r.description}
                                group={r}
                                displayCurrency={displayCurrency}
                                testId="bs-payable-group"
                              />
                            ))}
                          </CollapsibleAggregate>
                          {bs.current_liabilities.payables.unassigned_note && (
                            <div className="text-xs py-0.5 text-muted-foreground italic" data-testid="bs-payables-unassigned">
                              {bs.current_liabilities.payables.unassigned_note}
                            </div>
                          )}
                          {bs.current_liabilities.payables.settled_note && (
                            <div className="text-xs py-0.5 text-muted-foreground italic" data-testid="bs-payables-settled">
                              {bs.current_liabilities.payables.settled_note}
                            </div>
                          )}
                          {bs.current_liabilities.payables.unparseable_excluded_count > 0 && (
                            <div className="text-xs py-0.5 text-muted-foreground italic">
                              {bs.current_liabilities.payables.unparseable_excluded_count} excluded (amount unparseable)
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {bs.current_liabilities.tax && bs.current_liabilities.tax.rows.length > 0 && (
                      <div>
                        <AggregateRow
                          label="Tax"
                          converted={bs.current_liabilities.tax.subtotal}
                          displayCurrency={displayCurrency}
                          testId="bs-aggregate-tax"
                        />
                        <div className="pl-3 mt-0.5 space-y-0.5">
                          {bs.current_liabilities.tax.rows.filter((r) => !r.is_estimate).map((r, i) => (
                            <div key={i} className="flex items-center justify-between text-xs py-0.5" data-testid="bs-tax-row" title={r.note || undefined}>
                              <span>
                                {r.tax_label}
                                <span className="text-muted-foreground"> ({r.entity_name})</span>
                              </span>
                              <AmountPair native={r.required_amount} nativeCurrency={r.currency} converted={r.converted_value} displayCurrency={displayCurrency} />
                            </div>
                          ))}
                          {bs.current_liabilities.tax.rows.some((r) => r.is_estimate) && (
                            <div className="mt-1" data-testid="bs-tax-estimates">
                              <div className="flex items-center justify-between text-xs py-0.5 text-muted-foreground italic">
                                <span>Estimated tax on this year's profit (not in total)</span>
                                <span className="font-mono">{formatCurrency(bs.current_liabilities.tax.estimate_total, displayCurrency)}</span>
                              </div>
                              {bs.current_liabilities.tax.rows.filter((r) => r.is_estimate).map((r, i) => (
                                <div key={i} className="flex items-center justify-between text-xs py-0.5 pl-3 text-muted-foreground" data-testid="bs-tax-estimate-row" title={r.note || undefined}>
                                  <span>
                                    {r.tax_label}
                                    <span> ({r.entity_name})</span>
                                  </span>
                                  <AmountPair native={r.required_amount} nativeCurrency={r.currency} converted={r.converted_value} displayCurrency={displayCurrency} />
                                </div>
                              ))}
                            </div>
                          )}
                          {bs.current_liabilities.tax.note && (
                            <div className="text-xs py-0.5 text-amber-600 italic" data-testid="bs-tax-note">
                              {bs.current_liabilities.tax.note}
                            </div>
                          )}
                          {Number(bs.current_liabilities.tax.reserved_total) > 0 && (
                            <div className="flex items-center justify-between text-xs py-0.5 text-muted-foreground italic">
                              <span>Cash reserved against tax (already in Cash)</span>
                              <span className="font-mono">{formatCurrency(bs.current_liabilities.tax.reserved_total, displayCurrency)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {bs.current_liabilities.intercompany && (bs.current_liabilities.intercompany.rows.length > 0 || bs.current_liabilities.intercompany.eliminated_count > 0) && (
                      <div>
                        <AggregateRow
                          label="Intercompany Payables"
                          converted={bs.current_liabilities.intercompany.subtotal}
                          displayCurrency={displayCurrency}
                          testId="bs-aggregate-ic-payables"
                        />
                        <div className="pl-3 mt-0.5">
                          <CollapsibleAggregate
                            label="Intercompany Loans"
                            converted={bs.current_liabilities.intercompany.subtotal}
                            displayCurrency={displayCurrency}
                            testId="bs-source-group-intercompany-payable"
                          >
                            {bs.current_liabilities.intercompany.rows.map((r, i) => (
                              <HierarchyGroupRow
                                key={i}
                                label={`Intercompany loan to ${r.counterparty_name}`}
                                group={r}
                                displayCurrency={displayCurrency}
                                testId="bs-ic-payable-group"
                              />
                            ))}
                          </CollapsibleAggregate>
                          {bs.current_liabilities.intercompany.eliminated_count > 0 && (
                            <div className="flex items-center justify-between text-xs py-0.5 text-muted-foreground italic">
                              <span>Intra-group eliminated ({bs.current_liabilities.intercompany.eliminated_count})</span>
                              <span className="font-mono">{formatCurrency(bs.current_liabilities.intercompany.eliminated_total, displayCurrency)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {bs.current_liabilities.manual && bs.current_liabilities.manual.rows.length > 0 && (
                      <div>
                        <AggregateRow
                          label="Manual"
                          converted={bs.current_liabilities.manual.subtotal}
                          displayCurrency={displayCurrency}
                          testId="bs-aggregate-manual-liabilities"
                        />
                        <div className="pl-3 mt-0.5 space-y-0.5">
                          {bs.current_liabilities.manual.rows.map((r) => (
                            <div key={r.id} className="flex items-center justify-between text-xs py-0.5" data-testid="bs-manual-liability-row">
                              <span>
                                {r.name}
                                {scope === 'entity' && (
                                  <span className="text-muted-foreground"> ({r.entity_name || 'Unassigned'})</span>
                                )}
                              </span>
                              <AmountPair native={r.native_value} nativeCurrency={r.currency} converted={r.converted_value} displayCurrency={displayCurrency} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {(!bs.current_liabilities.intercompany || (bs.current_liabilities.intercompany.rows.length === 0 && !bs.current_liabilities.intercompany.eliminated_count)) &&
                      (!bs.current_liabilities.payables || bs.current_liabilities.payables.rows.length === 0) &&
                      (!bs.current_liabilities.tax || bs.current_liabilities.tax.rows.length === 0) &&
                      (!bs.current_liabilities.manual || bs.current_liabilities.manual.rows.length === 0) && (
                      <p className="text-xs text-muted-foreground">No current liabilities</p>
                    )}
                  </CardContent>
                </Card>
              </CardContent>
            )}
          </Card>
        </div>
      </div>

      {/* Refresh + Snapshot */}
      <div className="flex justify-end gap-2">
        {!isReadOnlyScope && (
          <Button variant="outline" size="sm" onClick={handleSnapshot} disabled={snapshotting}>
            {snapshotting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Camera className="h-4 w-4 mr-1" />}
            Snapshot Now
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={loadData} disabled={loading}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>
      </>
      )}
        </TabsContent>

        <TabsContent value="entries" className="space-y-6" data-testid="al-tab-content-entries">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Entries</CardTitle>
              <CardDescription>
                Every document behind every balance-sheet number, section by section. Each section reconciles
                to the cent against the balance sheet -- a discrepancy is shown in red, never hidden.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AlEntriesTab scope={scope} entityId={entityId} displayCurrency={displayCurrency} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="controls" className="space-y-6" data-testid="al-tab-content-controls">
          {/* Manual items: list with edit and delete-with-confirm */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Manual Items</CardTitle>
              <CardDescription>Assets and liabilities entered by hand, riding the same Net Assets series as everything else</CardDescription>
            </CardHeader>
            <CardContent>
              <ManualItemsList
                items={manualItems}
                categories={categories}
                scope={scope}
                onUpdated={loadData}
              />
            </CardContent>
          </Card>

          {/* Add item (never for the read-only group aggregate) */}
          {!isReadOnlyScope && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Add Item</CardTitle>
                <Button variant="outline" size="sm" onClick={() => setShowAddItem(!showAddItem)}>
                  <Plus className="h-4 w-4 mr-1" /> {showAddItem ? 'Cancel' : 'New Item'}
                </Button>
              </div>
            </CardHeader>
            {showAddItem && (
              <CardContent>
                <AddItemForm
                  scope={scope}
                  entityId={entityId}
                  categories={categories}
                  entities={entities}
                  onCreated={() => { setShowAddItem(false); loadData(); }}
                />
              </CardContent>
            )}
          </Card>
          )}

          {/* Category manager */}
          <CategoryManager categories={categories} onUpdated={loadData} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


function InlineValuationForm({ itemId, onDone, onCancel }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setSaving(true);
    try {
      await assetsLiabilitiesAPI.appendValuation(itemId, { date, value });
      toast.success('Valuation added');
      onDone();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="flex items-center gap-2 ml-5 my-1" onSubmit={handleSubmit}>
      <Input
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        className="h-6 text-xs w-[120px]"
      />
      <Input
        type="text"
        placeholder="Value"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="h-6 text-xs w-[100px]"
      />
      <Button type="submit" size="sm" className="h-6 text-xs px-2" disabled={saving}>
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
      </Button>
      <Button type="button" variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={onCancel}>
        Cancel
      </Button>
    </form>
  );
}


/**
 * Controls tab manual-items list: every al_items entry (raw, not the legacy
 * category-grouped shape), with its entity assignment shown explicitly
 * ("Unassigned" surfaced, never folded away), inline edit (name/category/
 * currency), and delete behind a two-step confirm.
 */
function ManualItemsList({ items, categories, scope, onUpdated }) {
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCategoryKey, setEditCategoryKey] = useState('');
  const [editCurrency, setEditCurrency] = useState('');
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
  const [addingValuationId, setAddingValuationId] = useState(null);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [saving, setSaving] = useState(false);

  // entity_name is resolved server-side (GET /al/items) from the stored
  // entity_id (a canonical slug, e.g. "eci") -- the entities store only
  // exposes UUID + name (GET /entities), not the slug, so it can't do this
  // lookup client-side.
  const entityLabel = (item) => {
    if (!item.entity_id) return 'Unassigned';
    return item.entity_name || item.entity_id;
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditCategoryKey(item.category_key);
    setEditCurrency(item.currency);
    setConfirmingDeleteId(null);
  };

  const saveEdit = async (itemId) => {
    setSaving(true);
    try {
      await assetsLiabilitiesAPI.updateItem(itemId, {
        name: editName.trim(),
        category_key: editCategoryKey,
        currency: editCurrency.toUpperCase(),
      });
      toast.success('Item updated');
      setEditingId(null);
      onUpdated();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (itemId) => {
    try {
      await assetsLiabilitiesAPI.deleteItem(itemId);
      toast.success('Item deleted');
      setConfirmingDeleteId(null);
      onUpdated();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  if (!items || items.length === 0) {
    return <p className="text-sm text-muted-foreground">No manual items yet.</p>;
  }

  return (
    <div className="space-y-1" data-testid="controls-manual-items-list">
      {items.map((item) => {
        const latest = [...(item.valuations || [])].sort((a, b) => b.date.localeCompare(a.date))[0];
        const isEditing = editingId === item.id;
        return (
          <div key={item.id} className="border rounded-md p-2 text-xs" data-testid={`controls-item-${item.id}`}>
            {isEditing ? (
              <div className="flex flex-wrap items-center gap-2">
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-7 text-xs w-[140px]" />
                <Select value={editCategoryKey} onValueChange={setEditCategoryKey}>
                  <SelectTrigger className="h-7 text-xs w-[140px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input value={editCurrency} onChange={(e) => setEditCurrency(e.target.value)} className="h-7 text-xs w-[70px]" />
                <Button size="sm" className="h-7 text-xs px-2" onClick={() => saveEdit(item.id)} disabled={saving} data-testid={`controls-item-${item.id}-save`}>
                  Save
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setEditingId(null)}>Cancel</Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {(item.valuations || []).length > 1 && (
                      <button
                        className="p-0 h-4 w-4 flex items-center justify-center text-muted-foreground hover:text-foreground"
                        onClick={() => setExpandedHistoryId(expandedHistoryId === item.id ? null : item.id)}
                        data-testid={`controls-item-${item.id}-history-toggle`}
                      >
                        {expandedHistoryId === item.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      </button>
                    )}
                    <span className="font-medium">{item.name}</span>
                    <Badge variant="outline" className="text-[9px]">{item.category_key}</Badge>
                    {scope === 'entity' && (
                      <Badge
                        variant="outline"
                        className={`text-[9px] ${item.entity_id ? '' : 'text-amber-600 border-amber-300'}`}
                        data-testid={`controls-item-${item.id}-entity`}
                      >
                        {entityLabel(item)}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">
                      {latest ? formatCurrency(latest.value, item.currency) : '--'}
                      {latest?.date && <span className="text-[10px] ml-1">({latest.date})</span>}
                    </span>
                    <button
                      className="text-emerald-600 hover:text-emerald-700 text-[10px]"
                      onClick={() => setAddingValuationId(addingValuationId === item.id ? null : item.id)}
                      data-testid={`controls-item-${item.id}-add-valuation`}
                    >
                      +val
                    </button>
                    <button
                      className="text-blue-500 hover:text-blue-700 text-[10px]"
                      onClick={() => startEdit(item)}
                      data-testid={`controls-item-${item.id}-edit`}
                    >
                      edit
                    </button>
                    {confirmingDeleteId === item.id ? (
                      <span className="flex items-center gap-1">
                        <button
                          className="text-red-600 text-[10px] font-medium"
                          onClick={() => handleDelete(item.id)}
                          data-testid={`controls-item-${item.id}-confirm-delete`}
                        >
                          Confirm?
                        </button>
                        <button className="text-muted-foreground text-[10px]" onClick={() => setConfirmingDeleteId(null)}>
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        className="text-red-400 hover:text-red-600 text-[10px]"
                        onClick={() => setConfirmingDeleteId(item.id)}
                        data-testid={`controls-item-${item.id}-delete`}
                      >
                        delete
                      </button>
                    )}
                  </div>
                </div>

                {addingValuationId === item.id && (
                  <InlineValuationForm
                    itemId={item.id}
                    onDone={() => { setAddingValuationId(null); onUpdated(); }}
                    onCancel={() => setAddingValuationId(null)}
                  />
                )}

                {expandedHistoryId === item.id && (
                  <div className="ml-5 border-l pl-2 my-1 space-y-0.5" data-testid={`controls-item-${item.id}-history`}>
                    {[...(item.valuations || [])].sort((a, b) => b.date.localeCompare(a.date)).map((v, i) => (
                      <div key={i} className="flex gap-2 text-[10px] text-muted-foreground">
                        <span>{v.date}</span>
                        <span className="font-mono">{formatCurrency(v.value, item.currency)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}


function AddItemForm({ scope, entityId, categories, entities, onCreated }) {
  const [categoryKey, setCategoryKey] = useState('');
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [value, setValue] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  // Entity assignment starts BLANK, not pre-filled from the page's current
  // entity: the operator must explicitly pick a company or Unassigned,
  // never inherit a silent default.
  const [entityAssignment, setEntityAssignment] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!categoryKey || !name.trim() || !value.trim()) {
      toast.error('Category, name, and value are required');
      return;
    }
    if (scope === 'entity' && !entityAssignment) {
      toast.error('Entity assignment is required: choose a company or Unassigned');
      return;
    }
    setSaving(true);
    try {
      await assetsLiabilitiesAPI.createItem({
        scope,
        entity_id: scope === 'entity' ? entityAssignment : undefined,
        category_key: categoryKey,
        name: name.trim(),
        currency: currency.toUpperCase(),
        value,
        date,
        notes: notes.trim() || undefined,
      });
      toast.success('Item created');
      onCreated();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="space-y-3" onSubmit={handleSubmit}>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground">Category</label>
          <Select value={categoryKey} onValueChange={setCategoryKey}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c.key} value={c.key}>
                  {c.label} ({c.kind})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 text-xs" placeholder="e.g. ISA Portfolio" />
        </div>
        {scope === 'entity' && (
          <div>
            <label className="text-xs text-muted-foreground">Entity</label>
            <Select value={entityAssignment} onValueChange={setEntityAssignment}>
              <SelectTrigger className="h-8 text-xs" data-testid="al-add-item-entity">
                <SelectValue placeholder="Select entity or Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                {entities.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name || e.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <label className="text-xs text-muted-foreground">Currency</label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {['GBP', 'EUR', 'USD', 'DKK'].map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Value</label>
          <Input value={value} onChange={(e) => setValue(e.target.value)} className="h-8 text-xs" placeholder="500000.00" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Effective date</label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 text-xs" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Notes (optional)</label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-8 text-xs" placeholder="Optional notes" />
        </div>
      </div>
      <Button type="submit" size="sm" disabled={saving}>
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
        Add Item
      </Button>
    </form>
  );
}


function CategoryManager({ categories, onUpdated }) {
  const [editing, setEditing] = useState(null);
  const [label, setLabel] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [saving, setSaving] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newKind, setNewKind] = useState('asset');

  const handleUpdate = async (key) => {
    setSaving(true);
    try {
      const updates = {};
      if (label.trim()) updates.label = label.trim();
      if (sortOrder !== '') updates.sort_order = Number(sortOrder);
      await assetsLiabilitiesAPI.updateCategory(key, updates);
      toast.success('Category updated');
      setEditing(null);
      onUpdated();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!newKey.trim() || !newLabel.trim()) return;
    setSaving(true);
    try {
      await assetsLiabilitiesAPI.createCategory({
        key: newKey.trim().toLowerCase().replace(/\s+/g, '_'),
        label: newLabel.trim(),
        kind: newKind,
        sort_order: 99,
      });
      toast.success('Category created');
      setShowNew(false);
      setNewKey('');
      setNewLabel('');
      onUpdated();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Categories</CardTitle>
          <Button variant="outline" size="sm" onClick={() => setShowNew(!showNew)}>
            <Plus className="h-4 w-4 mr-1" /> {showNew ? 'Cancel' : 'New Category'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-1">
        {categories.map((cat) => (
          <div key={cat.key} className="flex items-center justify-between text-xs py-1 border-b last:border-0">
            {editing === cat.key ? (
              <div className="flex items-center gap-2">
                <Input value={label} onChange={(e) => setLabel(e.target.value)} className="h-6 text-xs w-[120px]" />
                <Input value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="h-6 text-xs w-[50px]" placeholder="#" />
                <Button size="sm" className="h-6 text-xs px-2" onClick={() => handleUpdate(cat.key)} disabled={saving}>Save</Button>
                <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={() => setEditing(null)}>Cancel</Button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span>{cat.label}</span>
                  <Badge variant="outline" className="text-[9px]">{cat.kind}</Badge>
                  {cat.linked_source && (
                    <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200">
                      {cat.linked_source}
                    </Badge>
                  )}
                </div>
                <button
                  className="text-blue-500 hover:text-blue-700 text-[10px]"
                  onClick={() => { setEditing(cat.key); setLabel(cat.label); setSortOrder(String(cat.sort_order ?? '')); }}
                >
                  edit
                </button>
              </>
            )}
          </div>
        ))}

        {showNew && (
          <div className="flex items-center gap-2 pt-2">
            <Input value={newKey} onChange={(e) => setNewKey(e.target.value)} className="h-6 text-xs w-[100px]" placeholder="key" />
            <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} className="h-6 text-xs w-[120px]" placeholder="Label" />
            <Select value={newKind} onValueChange={setNewKind}>
              <SelectTrigger className="h-6 text-xs w-[80px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="asset">asset</SelectItem>
                <SelectItem value="liability">liability</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" className="h-6 text-xs px-2" onClick={handleCreate} disabled={saving}>Add</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
